import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Lock, User, Phone, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

export default function AuthModal({ isOpen, onClose }) {
  const { 
    loginWithGoogle, 
    loginWithEmail, 
    registerWithEmail, 
    resetPassword,
    authError, 
    clearAuthError 
  } = useAuth();
  const { t } = useLanguage();
  const [mode, setMode] = useState("login"); // "login", "register", "forgot"
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Form fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setName("");
    setPhone("");
    setLocalError(null);
    setSuccessMessage(null);
    clearAuthError();
  };

  const switchMode = (newMode) => {
    resetForm();
    setMode(newMode);
  };

  const handleClose = () => {
    resetForm();
    setMode("login");
    onClose();
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setLocalError(null);
    const result = await loginWithGoogle();
    setIsLoading(false);
    
    if (result.success) {
      handleClose();
    } else if (result.error) {
      setLocalError(result.error);
    }
  };

  const validateForm = () => {
    if (!email || !email.includes("@")) {
      setLocalError(t("auth_error_invalid_email") || "Por favor ingresa un email válido");
      return false;
    }
    if (mode !== "forgot" && (!password || password.length < 6)) {
      setLocalError(t("auth_error_password_short") || "La contraseña debe tener al menos 6 caracteres");
      return false;
    }
    return true;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    let result;
    if (mode === "login") {
      result = await loginWithEmail(email, password);
      if (result.success) {
        handleClose();
      } else if (result.needsVerification) {
        setLocalError(result.error);
      } else if (result.error) {
        setLocalError(result.error);
      }
    } else if (mode === "register") {
      result = await registerWithEmail(email, password, name || null, phone || null);
      if (result.success) {
        setSuccessMessage(result.message);
        // Don't close - show success message
      } else if (result.error) {
        setLocalError(result.error);
      }
    } else if (mode === "forgot") {
      result = await resetPassword(email);
      if (result.success) {
        setSuccessMessage(result.message);
      } else if (result.error) {
        setLocalError(result.error);
      }
    }
    
    setIsLoading(false);
  };

  const getSubmitLabel = () => {
    if (isLoading) return t("auth_loading") || "Cargando...";
    if (mode === "login") return t("auth_login_btn") || "Iniciar Sesión";
    if (mode === "register") return t("auth_register_btn") || "Crear Cuenta";
    return t("auth_reset_btn") || "Enviar Enlace";
  };

  const displayError = localError || authError;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl max-w-md w-full p-6 relative shadow-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="auth-modal-close"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="text-center mb-6">
            <h2 className="font-gluten text-2xl text-agave mb-2">
              {mode === "login" && (t("auth_login_title") || "Iniciar Sesión")}
              {mode === "register" && (t("auth_register_title") || "Crear Cuenta")}
              {mode === "forgot" && (t("auth_forgot_title") || "Recuperar Contraseña")}
            </h2>
            <p className="text-gray-500 text-sm">
              {mode === "login" && (t("auth_login_subtitle") || "Accede a tu cuenta para ver tu historial")}
              {mode === "register" && (t("auth_register_subtitle") || "Crea una cuenta para guardar tus datos")}
              {mode === "forgot" && (t("auth_forgot_subtitle") || "Te enviaremos un enlace para restablecer tu contraseña")}
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 text-green-700 text-sm p-4 rounded-xl border border-green-200 mb-4 flex items-start gap-3" data-testid="auth-success">
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">{successMessage}</p>
                {mode === "register" && (
                  <p className="mt-1 text-green-600">Revisa tu bandeja de entrada y spam.</p>
                )}
              </div>
            </div>
          )}

          {/* Google Login Button - only show for login/register */}
          {mode !== "forgot" && !successMessage && (
            <>
              <Button
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className="w-full bg-white border-2 border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-xl py-6 mb-4 flex items-center justify-center gap-3"
                data-testid="auth-google-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t("auth_google_btn") || "Continuar con Google"}
              </Button>

              {/* Divider */}
              <div className="flex items-center gap-4 my-4">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-gray-400 text-sm">{t("auth_or") || "o"}</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>
            </>
          )}

          {/* Email Form */}
          {!successMessage && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              {/* Name field (only for register) */}
              {mode === "register" && (
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder={t("auth_name_placeholder") || "Nombre (opcional)"}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 py-5 rounded-xl border-gray-200 focus:border-agave focus:ring-agave"
                    data-testid="auth-name-input"
                  />
                </div>
              )}

              {/* Email field */}
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  placeholder={t("auth_email_placeholder") || "Correo electrónico"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 py-5 rounded-xl border-gray-200 focus:border-agave focus:ring-agave"
                  data-testid="auth-email-input"
                />
              </div>

              {/* Password field (not for forgot mode) */}
              {mode !== "forgot" && (
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("auth_password_placeholder") || "Contraseña"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 pr-10 py-5 rounded-xl border-gray-200 focus:border-agave focus:ring-agave"
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              )}

              {/* Phone field (only for register) */}
              {mode === "register" && (
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="tel"
                    placeholder={t("auth_phone_placeholder") || "Teléfono (opcional)"}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="pl-10 py-5 rounded-xl border-gray-200 focus:border-agave focus:ring-agave"
                    data-testid="auth-phone-input"
                  />
                </div>
              )}

              {/* Forgot password link */}
              {mode === "login" && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => switchMode("forgot")}
                    className="text-sm text-agave hover:underline"
                    data-testid="auth-forgot-link"
                  >
                    {t("auth_forgot_password") || "¿Olvidaste tu contraseña?"}
                  </button>
                </div>
              )}

              {/* Error message */}
              {displayError && (
                <div className="bg-red-50 text-red-600 text-sm p-3 rounded-xl border border-red-200 flex items-start gap-2" data-testid="auth-error">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{displayError}</span>
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-mexican hover:bg-mexican/90 text-white font-bold rounded-xl py-6"
                data-testid="auth-submit-btn"
              >
                {getSubmitLabel()}
              </Button>
            </form>
          )}

          {/* Back to login after success */}
          {successMessage && (
            <Button
              onClick={() => switchMode("login")}
              className="w-full bg-agave hover:bg-agave/90 text-white font-bold rounded-xl py-6"
            >
              {t("auth_back_to_login") || "Volver a Iniciar Sesión"}
            </Button>
          )}

          {/* Switch mode links */}
          {!successMessage && (
            <div className="text-center mt-4">
              {mode === "login" && (
                <p className="text-gray-500 text-sm">
                  {t("auth_no_account") || "¿No tienes cuenta?"}{" "}
                  <button
                    onClick={() => switchMode("register")}
                    className="text-agave font-semibold hover:underline"
                    data-testid="auth-switch-register"
                  >
                    {t("auth_create_account") || "Crear cuenta"}
                  </button>
                </p>
              )}
              {mode === "register" && (
                <p className="text-gray-500 text-sm">
                  {t("auth_have_account") || "¿Ya tienes cuenta?"}{" "}
                  <button
                    onClick={() => switchMode("login")}
                    className="text-agave font-semibold hover:underline"
                    data-testid="auth-switch-login"
                  >
                    {t("auth_login_link") || "Iniciar sesión"}
                  </button>
                </p>
              )}
              {mode === "forgot" && (
                <p className="text-gray-500 text-sm">
                  <button
                    onClick={() => switchMode("login")}
                    className="text-agave font-semibold hover:underline"
                  >
                    {t("auth_back_to_login") || "Volver a Iniciar Sesión"}
                  </button>
                </p>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
